import { useState, useRef, useEffect } from "react";
import type { MsgType, SendStatus } from "../types";

export interface FilterState {
  search: string;
  dateFrom: string;
  dateTo: string;
  types: MsgType[];
  statuses: SendStatus[];
  sortBy: "sentAt" | "count" | "cost";
  sortDir: "desc" | "asc";
}

interface Props {
  filter: FilterState;
  onChange: (f: FilterState) => void;
  totalCount: number;
  filteredCount: number;
}

const MSG_TYPES: MsgType[] = ["SMS", "LMS", "MMS"];
const STATUSES: { value: SendStatus; label: string }[] = [
  { value: "success", label: "성공" },
  { value: "fail", label: "실패" },
  { value: "scheduled", label: "예약" },
  { value: "pending", label: "발송 중" },
];

const DATE_PRESETS = [
  { label: "오늘", days: 0 },
  { label: "7일", days: 7 },
  { label: "30일", days: 30 },
  { label: "90일", days: 90 },
];

function getDateStr(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split("T")[0];
}

export default function HistoryFilters({ filter, onChange, totalCount, filteredCount }: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setShowSort(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const update = (partial: Partial<FilterState>) => onChange({ ...filter, ...partial });

  const toggleType = (t: MsgType) => {
    const next = filter.types.includes(t) ? filter.types.filter((x) => x !== t) : [...filter.types, t];
    update({ types: next });
  };

  const toggleStatus = (s: SendStatus) => {
    const next = filter.statuses.includes(s) ? filter.statuses.filter((x) => x !== s) : [...filter.statuses, s];
    update({ statuses: next });
  };

  const applyPreset = (days: number) => {
    if (days === 0) {
      const today = getDateStr(0);
      update({ dateFrom: today, dateTo: today });
    } else {
      update({ dateFrom: getDateStr(days), dateTo: getDateStr(0) });
    }
  };

  const hasActiveFilters =
    filter.search ||
    filter.dateFrom ||
    filter.dateTo ||
    filter.types.length > 0 ||
    filter.statuses.length > 0;

  const sortLabels: Record<string, string> = {
    sentAt: "발송 시각",
    count: "수신자 수",
    cost: "비용",
  };

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl md:rounded-2xl overflow-hidden">
      {/* Main filter row */}
      <div className="flex flex-col gap-2 md:gap-3 p-3 md:p-4">
        {/* Search */}
        <div className="relative w-full">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
            <i className="ri-search-line text-gray-500 text-xs md:text-sm"></i>
          </div>
          <input
            type="text"
            value={filter.search}
            onChange={(e) => update({ search: e.target.value })}
            placeholder="메시지 내용, 수신번호 검색..."
            className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl pl-9 pr-4 py-2 md:py-2.5 text-white text-xs md:text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all"
          />
          {filter.search && (
            <button
              onClick={() => update({ search: "" })}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-xs md:text-sm"></i>
            </button>
          )}
        </div>

        {/* Second row: status pills + sort + advanced */}
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
          {/* Quick status pills */}
          {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => toggleStatus(s.value)}
              className={`px-2 py-1 md:px-3 md:py-1.5 rounded-full text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                filter.statuses.includes(s.value)
                  ? s.value === "success"
                    ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                    : s.value === "fail"
                    ? "bg-red-500/20 text-red-400 border border-red-500/30"
                    : s.value === "scheduled"
                    ? "bg-violet-500/20 text-violet-400 border border-violet-500/30"
                    : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  : "bg-white/[0.05] text-gray-400 hover:text-white border border-white/[0.08]"
              }`}
            >
              {s.label}
            </button>
          ))}

          {/* Sort dropdown */}
          <div className="relative" ref={sortRef}>
            <button
              onClick={() => setShowSort(!showSort)}
              className="flex items-center gap-1 bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white px-2 py-1 md:px-3 md:py-1.5 rounded-lg md:rounded-xl text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
            >
              <i className={`${filter.sortDir === "desc" ? "ri-sort-desc" : "ri-sort-asc"} text-[10px] md:text-sm`}></i>
              {sortLabels[filter.sortBy]}
              <i className={`ri-arrow-down-s-line text-[10px] md:text-sm transition-transform ${showSort ? "rotate-180" : ""}`}></i>
            </button>
            {showSort && (
              <div className="absolute right-0 top-full mt-1 w-40 md:w-44 bg-[#0d0d1a] border border-white/[0.12] rounded-xl overflow-hidden z-20">
                {(["sentAt", "count", "cost"] as const).map((key) => (
                  <button
                    key={key}
                    onClick={() => {
                      if (filter.sortBy === key) {
                        update({ sortDir: filter.sortDir === "desc" ? "asc" : "desc" });
                      } else {
                        update({ sortBy: key, sortDir: "desc" });
                      }
                      setShowSort(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm cursor-pointer transition-colors hover:bg-white/[0.05] ${
                      filter.sortBy === key ? "text-violet-400" : "text-gray-400"
                    }`}
                  >
                    {sortLabels[key]}
                    {filter.sortBy === key && (
                      <i className={`${filter.sortDir === "desc" ? "ri-arrow-down-line" : "ri-arrow-up-line"} text-[10px] md:text-xs`}></i>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Advanced toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`flex items-center gap-1 px-2 py-1 md:px-3 md:py-1.5 rounded-lg md:rounded-xl text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
              showAdvanced || filter.dateFrom || filter.types.length > 0
                ? "bg-violet-500/20 text-violet-400 border border-violet-500/30"
                : "bg-white/[0.05] text-gray-400 hover:text-white border border-white/[0.08]"
            }`}
          >
            <i className="ri-equalizer-2-line text-[10px] md:text-sm"></i>
            상세 필터
            {(filter.dateFrom || filter.types.length > 0) && (
              <span className="w-1 h-1 md:w-1.5 md:h-1.5 bg-violet-400 rounded-full"></span>
            )}
          </button>
        </div>
      </div>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="border-t border-white/[0.07] px-3 md:px-4 py-3 md:py-4 space-y-3 md:space-y-4">
          {/* Date range */}
          <div>
            <p className="text-gray-400 text-[10px] md:text-xs font-medium mb-1.5 md:mb-2">날짜 범위</p>
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(p.days)}
                  className="px-2 py-1 md:px-3 md:py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white hover:border-white/[0.2] text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
                >
                  {p.label}
                </button>
              ))}
              <div className="flex items-center gap-1.5 md:gap-2">
                <input
                  type="date"
                  value={filter.dateFrom}
                  onChange={(e) => update({ dateFrom: e.target.value })}
                  className="bg-white/[0.05] border border-white/[0.1] rounded-lg px-2 py-1 md:px-3 md:py-1.5 text-white text-[10px] md:text-xs focus:outline-none focus:border-violet-500/50 cursor-pointer transition-all"
                />
                <span className="text-gray-600 text-[10px] md:text-xs">~</span>
                <input
                  type="date"
                  value={filter.dateTo}
                  onChange={(e) => update({ dateTo: e.target.value })}
                  className="bg-white/[0.05] border border-white/[0.1] rounded-lg px-2 py-1 md:px-3 md:py-1.5 text-white text-[10px] md:text-xs focus:outline-none focus:border-violet-500/50 cursor-pointer transition-all"
                />
              </div>
            </div>
          </div>

          {/* Message type */}
          <div>
            <p className="text-gray-400 text-[10px] md:text-xs font-medium mb-1.5 md:mb-2">메시지 유형</p>
            <div className="flex items-center gap-1.5 md:gap-2">
              {MSG_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => toggleType(t)}
                  className={`px-3 py-1 md:px-4 md:py-1.5 rounded-lg text-[10px] md:text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                    filter.types.includes(t)
                      ? t === "SMS"
                        ? "bg-violet-500/30 text-violet-300 border border-violet-500/40"
                        : t === "LMS"
                        ? "bg-pink-500/30 text-pink-300 border border-pink-500/40"
                        : "bg-amber-500/30 text-amber-300 border border-amber-500/40"
                      : "bg-white/[0.05] text-gray-400 hover:text-white border border-white/[0.08]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filter summary bar */}
      <div className="border-t border-white/[0.06] px-3 md:px-4 py-2 md:py-2.5 flex items-center justify-between">
        <p className="text-gray-500 text-[10px] md:text-xs">
          전체 <span className="text-white font-semibold">{totalCount}</span>건 중{" "}
          <span className="text-violet-400 font-semibold">{filteredCount}</span>건 표시
        </p>
        {hasActiveFilters && (
          <button
            onClick={() =>
              onChange({
                search: "",
                dateFrom: "",
                dateTo: "",
                types: [],
                statuses: [],
                sortBy: "sentAt",
                sortDir: "desc",
              })
            }
            className="flex items-center gap-1 text-[10px] md:text-xs text-red-400/70 hover:text-red-400 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-filter-off-line text-[10px] md:text-sm"></i>
            필터 초기화
          </button>
        )}
      </div>
    </div>
  );
}